import { useState } from "react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { useCreateEvent } from "../events/useCreateEvent.js";
import { useEditEvent } from "../events/useEditEvent.js";
import { getCountries, getCities } from "../constants/geography.js";

function CreateEventForm({ eventToEdit, onCloseModal }) {
    // Convert dateTime from DB
    const convertToDateTimeLocal = (isoString) => {
        const date = new Date(isoString);
        return format(date, "yyyy-MM-dd'T'HH:mm");
    };

    const isEditSession = Boolean(eventToEdit);

    let editId;
    let editValues = {};

    if (isEditSession) {
        ({ id: editId, ...editValues } = eventToEdit);

        if (editValues.startDatetime) {
            editValues.startDatetime = convertToDateTimeLocal(editValues.startDatetime);
        }
    }

    const [selectedCountry, setSelectedCountry] = useState("Australia");
    const [selectedCity, setSelectedCity] = useState("");

    // Venue: list vs custom
    const VENUE_OPTIONS = [
        
        "Chasers Nightclub", // Melbourne
        "Inflation", // Melbourne
        
        "Aura Nightclub", // Sydney
        "ARQ", // Sydney
        "Ivy", // Sydney
        "Jimmy's Rooftop",// Sydney
        
        "The Court", // Perth
    ];
    const [venueMode, setVenueMode] = useState("list");
    const [selectedVenue, setSelectedVenue] = useState("");

    const countries = getCountries();
    const cities = selectedCountry ? getCities(selectedCountry) : [];

    // Default Start Date Time: today at 10:00 PM
    const defaultStartDatetime = (() => {
        const d = new Date();
        d.setHours(22, 0, 0, 0);
        return format(d, "yyyy-MM-dd'T'HH:mm");
    })();

    const {
        register,
        handleSubmit,
        reset,
        formState,
        setValue,
        watch,
    } = useForm({
        defaultValues: isEditSession ? editValues : { startDatetime: defaultStartDatetime },
    });

    const currentVenue = watch("venue");

    const { errors } = formState;
    const { isCreating, createEvent } = useCreateEvent();
    const { isEditing, editEvent } = useEditEvent();

    const isWorking = isCreating || isEditing;

    function onSubmit(data) {
        if (isEditSession) {
            editEvent({ newCabinData: data, id: editId }, {
                onSuccess: () => {
                    reset();
                    onCloseModal?.();
                }
            });
        } else {
            createEvent(data, {
                onSuccess: () => {
                    reset();
                    onCloseModal?.();
                }
            });
        }
    }

    function onError(data) {
        console.log(data);
        alert(data);
    }

    return (
        <div className="max-w-lg mx-auto p-6 bg-white shadow-lg rounded-lg">
            <h2 className="text-xl font-bold mb-4">
                {isEditSession ? "Edit Event" : "Add New Event"}
            </h2>
            <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-4">
                <label htmlFor="name">Event Name</label>
                {errors?.name?.message && <p className="text-red-500">{errors.name.message}</p>}
                <input disabled={isWorking} type="text" id="name" className="w-full p-2 border rounded"
                       {...register("name", { required: "This field is required" })} />

                {/* Country Dropdown */}
                <label htmlFor="country">Country</label>
                <select
                    disabled={isWorking}
                    id="country"
                    className="w-full p-2 border rounded"
                    {...register("country", { required: "This field is required" })}
                    value={selectedCountry}
                    onChange={(e) => {
                        const country = e.target.value;
                        const newCities = getCities(country);
                        setSelectedCountry(country);
                        setSelectedCity(newCities[0] || "");
                    }}

                >
                    {countries.map((country) => (
                        <option key={country} value={country}>
                            {country}
                        </option>
                    ))}
                </select>

                {/* City Dropdown */}
                <label htmlFor="city">City</label>
                <select
                    disabled={isWorking}
                    id="city"
                    className="w-full p-2 border rounded"
                    {...register("city", { required: "This field is required" })}
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                >
                    {cities.map((city) => (
                        <option key={city} value={city}>
                            {city}
                        </option>
                    ))}
                </select>

                {/* Venue: choose from list or type custom */}
                <label>Venue</label>
                {errors?.venue?.message && <p className="text-red-500">{errors.venue.message}</p>}

                <div className="flex gap-2">
                    <button
                        type="button"
                        disabled={isWorking}
                        onClick={() => {
                            setVenueMode("list");
                            // if switching to list and current venue matches an option, preselect it
                            const match = VENUE_OPTIONS.includes(currentVenue) ? currentVenue : "";
                            setSelectedVenue(match);
                            if (match) setValue("venue", match, { shouldValidate: true, shouldDirty: true });
                        }}
                        className={`flex-1 p-2 border rounded ${venueMode === "list" ? "bg-yellow-100" : "bg-white"}`}
                    >
                        Pick from list
                    </button>

                    <button
                        type="button"
                        disabled={isWorking}
                        onClick={() => {
                            setVenueMode("custom");
                            setSelectedVenue("");
                            // keep whatever is currently in "venue" as the custom text
                        }}
                        className={`flex-1 p-2 border rounded ${venueMode === "custom" ? "bg-yellow-100" : "bg-white"}`}
                    >
                        Custom
                    </button>
                </div>

                {venueMode === "list" ? (
                    <select
                        disabled={isWorking}
                        className="w-full p-2 border rounded"
                        value={selectedVenue}
                        onChange={(e) => {
                            const v = e.target.value;
                            setSelectedVenue(v);
                            setValue("venue", v, { shouldValidate: true, shouldDirty: true });
                        }}
                    >
                        <option value="" disabled>Select a venue...</option>
                        {VENUE_OPTIONS.map((v) => (
                            <option key={v} value={v}>
                                {v}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        disabled={isWorking}
                        type="text"
                        id="venue"
                        className="w-full p-2 border rounded"
                        placeholder="Type venue name..."
                        {...register("venue", { required: "This field is required" })}
                    />
                )}

                <label htmlFor="description">Description</label>
                <textarea
                    disabled={isWorking}
                    id="description"
                    className="w-full p-2 border rounded resize-y min-h-[4rem]"
                    {...register("description", { required: "This field is required" })}
                />

                <label htmlFor="eventUrl">Event Url</label>
                <input disabled={isWorking} type="text" id="eventUrl" className="w-full p-2 border rounded"
                       {...register("eventUrl", { required: "This field is required" })} />

                <label htmlFor="ticketsUrl">Tickets Url</label>
                <input disabled={isWorking} type="text" id="ticketsUrl" className="w-full p-2 border rounded"
                       {...register("ticketsUrl", { required: "This field is required" })} />

                <label htmlFor="type">Type</label>
                <input disabled={isWorking} type="text" value="Party" id="type" className="w-full p-2 border rounded"
                       {...register("type", { required: "This field is required" })} />

                <label htmlFor="startDatetime" className="block font-medium">Start Date Time</label>
                <input
                    disabled={isWorking}
                    type="datetime-local"
                    id="startDatetime"
                    className="w-full p-2 border rounded"
                    {...register("startDatetime", { required: true })}
                />

                <button disabled={isWorking} type="submit"
                        className="w-full p-2 !bg-blue-300 text-white rounded">
                    {isEditSession ? "Edit Event" : "Create Event"}
                </button>
                <button type="reset" onClick={() => onCloseModal?.()}
                        className="w-full p-2 !bg-red-300 text-white rounded">
                    Cancel
                </button>
            </form>
        </div>
    );
}

export default CreateEventForm;